import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  Popover,
  TablePagination, 
  TableSortLabel,
  Collapse,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import DownloadIcon from '@mui/icons-material/Download';
import DateRangePickerComponent from '../KeyAnalytics/DateRangePickerComponent';
import useFetchData from '../Hooks/useFetchData';
import { formatDateRange } from '../Hooks/dateUtils';
import './EmployeeComponents.css';

export default function ROIComponent({ employeeName }) {
  const [dateRange, setDateRange] = useState([null, null]);
  const [anchorEl, setAnchorEl] = useState(null);

  // pagination
  const [page, setPage] = useState(0);

  const apiUrl = process.env.REACT_APP_API_URL;
  const endPoint = '/api/rep_roi';
  const { stats, loading, fetchData } = useFetchData(apiUrl, endPoint);
  const [expanded, setExpanded] = useState(new Set());
  const [orderBy, setOrderBy] = useState('return_on_investment');
  const [order, setOrder] = useState('desc');
  const [loadingDownload, setLoadingDownload] = useState(false);
  const rows = stats.roi_data ?? [];
  const rowsPerPage = 5;

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedRows = [...rows].sort((a, b) => {
    let valA = a[orderBy];
    let valB = b[orderBy];

    if (valA == null) return 1;
    if (valB == null) return -1;

    // Try parsing both as floats
    const numA = parseFloat(valA);
    const numB = parseFloat(valB);

    const bothNumbers = !isNaN(numA) && !isNaN(numB);

    if (bothNumbers) {
      return order === 'asc' ? numA - numB : numB - numA;
    }

    // Fallback to string compare
    const strA = String(valA).toLowerCase();
    const strB = String(valB).toLowerCase();

    return order === 'asc'
      ? strA.localeCompare(strB)
      : strB.localeCompare(strA);
  });

  const paginatedRows = sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const rowId = (row, idx) => `${row.practice}-${row.practice_postcode || ''}-${idx}`;

  const toggleExpand = id => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // format helpers
  const fmtPacks = n => (typeof n === 'number' ? n.toFixed(2) : `${n}`);
  const fmtRoi = v => {
    const n = typeof v === 'number' ? v : parseFloat(v);
    return isNaN(n) ? '0' : n.toLocaleString();
  };

  useEffect(() => {
    if (!employeeName) return;
    const [from_date, to_date] = dateRange;
    fetchData({ employee_name: employeeName, from_date, to_date });
  }, [employeeName, dateRange]);

  // reset to first page whenever filters/data change
  useEffect(() => {
    setPage(0);
  }, [employeeName, dateRange, stats?.roi_data?.length]);

  const handleFilterClick = e => setAnchorEl(e.currentTarget);
  const handleFilterClose = () => setAnchorEl(null);

  const handleApply = filters => {
    setDateRange([filters.from_date, filters.to_date]);
    handleFilterClose();
  };

  const handleReset = () => {
    setDateRange([null, null]);
    handleFilterClose();
  };

  const handleChangePage = (_e, newPage) => setPage(newPage);

  const open = Boolean(anchorEl);
  const id = open ? 'popover-gp' : undefined;
  const formattedRange = formatDateRange(dateRange);

  const downloadDataAsPDF = async () => {
    setLoadingDownload(true);
    const [from_date, to_date] = dateRange;
    try {
      const response = await fetch(`${apiUrl}/api/export_rep_roi`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          employee_name: employeeName, 
          from_date: from_date, 
          to_date:to_date, 
          sort_column:orderBy,
          sort_order:order}),
      });

      if (!response.ok) {
        throw new Error('Failed to download PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${employeeName}-roi.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error('PDF download error:', error);
      alert('There was an error downloading the PDF. Please try again.');
    } finally {
      setLoadingDownload(false);
    }
  };

  return (
    <>
      <Tooltip title="Return on Investment" arrow>
        <Card className="employee-card">
          <CardContent>
            <Box className="employee-header"
                sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                mb: 2 // optional: adds bottom margin
              }}
            >
              <Typography variant="h6" className="employee-title">
                Return on Investment
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton size="small" onClick={downloadDataAsPDF} className="employee-icon-button">
                  {loadingDownload ? <CircularProgress size={20} /> : <DownloadIcon/>}
                </IconButton>
                <IconButton size="small" onClick={handleFilterClick} className="employee-icon-button">
                  <FilterListIcon />
                </IconButton>
              </Box>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 600 }}>
                    <TableHead>
                      <TableRow className="employee-table-head">
                        <TableCell>Practice</TableCell>
                        <TableCell align="right">
                          <TableSortLabel
                            active={orderBy === 'return_on_investment'}
                            direction={orderBy === 'return_on_investment' ? order : 'asc'}
                            onClick={() => handleSort('return_on_investment')}
                          >
                            Packs/Contact
                          </TableSortLabel>
                        </TableCell>
                        <Tooltip title="Prescribers are defined as GPs, Advanced Nurse Practitioners, PCN Pharmacists" arrow>
                          <TableCell align="right">
                            <TableSortLabel
                              active={orderBy === 'contact'}
                              direction={orderBy === 'contact' ? order : 'asc'}
                              onClick={() => handleSort('contact')}
                            >
                              Prescriber Contacts
                            </TableSortLabel>
                          </TableCell>
                        </Tooltip>
                        <TableCell align="right">
                          <TableSortLabel
                            active={orderBy === 'rx_prescriptions'}
                            direction={orderBy === 'rx_prescriptions' ? order : 'asc'}
                            onClick={() => handleSort('rx_prescriptions')}
                          >
                            Total Packs Prescribed
                          </TableSortLabel>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedRows.map((row, i) => {
                          const id = rowId(row, i);
                          const isOpen = expanded.has(id);

                          return (
                          <React.Fragment key={id}>
                              <TableRow
                              className="employee-table-row"
                              hover
                              onClick={() => toggleExpand(id)}
                              sx={{ cursor: 'pointer' }}
                              aria-expanded={isOpen}
                              aria-controls={`products-${id}`}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  toggleExpand(id);
                                  }
                              }}
                              >
                              <TableCell>{row.practice}</TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                    {fmtRoi(row.return_on_investment)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                    {fmtRoi(row.contact)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                    {fmtRoi(row.rx_prescriptions.toFixed(2))}
                                </Typography>
                              </TableCell>
                              </TableRow>

                              <TableRow>
                              <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
                                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                    <Box
                                        sx={{
                                        mt: 0.5,
                                        mb: 1,
                                        p: 1.25,
                                        borderRadius: 1,
                                        bgcolor: (t) => t.palette.action.hover,
                                        borderLeft: (t) => `3px solid ${t.palette.primary.main}`,
                                        }}
                                    >
                                        {Array.isArray(row.products) && row.products.length > 0 ? (
                                        <Box
                                            id={`products-${id}`}
                                            aria-label="products"
                                            sx={{
                                            display: 'grid',
                                            rowGap: 0.5,
                                            }}
                                        >
                                            {row.products.map((p, idx) => (
                                            <Box
                                                key={`${id}-p-${idx}`}
                                                sx={{
                                                display: 'grid',
                                                gridTemplateColumns: '1fr auto',
                                                alignItems: 'center',
                                                py: 0.5,
                                                ...(idx !== row.products.length - 1 && {
                                                    borderBottom: (t) => `1px dashed ${t.palette.divider}`,
                                                }),
                                                }}
                                            >
                                                <Typography variant="body2" sx={{ pr: 1 }}>
                                                {p.name}
                                                </Typography>
                                                <Typography
                                                variant="body2"
                                                sx={{ fontVariantNumeric: 'tabular-nums', pl: 1 }}
                                                >
                                                {fmtPacks(p.packs)}
                                                </Typography>
                                            </Box>
                                            ))}
                                        </Box>
                                        ) : (
                                        <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                            No products recorded for this row.
                                        </Typography>
                                        )}

                                        <Box
                                        sx={{
                                            mt: 1,
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: 2,
                                            fontSize: '0.75rem',
                                            color: (t) => t.palette.text.secondary,
                                        }}
                                        >
                                        {row.practice_postcode && (
                                            <Typography variant="caption">
                                            Postcode: {row.practice_postcode}
                                            </Typography>
                                        )}
                                        </Box>
                                    </Box>
                                    </Collapse>
                              </TableCell>
                              </TableRow>
                          </React.Fragment>
                          );
                      })}
                    </TableBody>
                  </Table>
                </Box>
                <TablePagination
                  component="div"
                  count={rows.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[10]} // fixed at 10
                />
              </>
            )}

            <Typography variant="body2" className="employee-date-range">
              {formattedRange}
            </Typography>
          </CardContent>
        </Card>
      </Tooltip>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ className: 'employee-popover' }}
      >
        <Box sx={{ p: 2, width: 300 }}>
          <DateRangePickerComponent
            dateRange={dateRange}
            setDateRange={setDateRange}
            onApply={handleApply}
            onReset={handleReset}
          />
        </Box>
      </Popover>
    </>
  );
}
